/**
 * Lake Charles, Louisiana - City Configuration
 *
 * This config defines all Lake Charles-specific content, branding, and settings.
 * Inherits Louisiana state defaults and overrides where needed.
 *
 * Build command: CITY=louisiana/lake-charles npm run build:city
 * Dev command: CITY=louisiana/lake-charles npm run dev:city
 * Deploys to: lakecharles.toolboxlogic.com
 */

import type { CityConfig } from '@core/types/city-config';
import { louisianaStateConfig, mergeStateConfig } from '../state.config';

// Lake Charles-specific overrides
const lakeCharlesOverrides = {
  // City identity
  identity: {
    cityName: 'Lake Charles',
    stateName: 'Louisiana',
    stateAbbreviation: 'LA',
    fullName: 'Lake Charles, Louisiana',
    // 2023 Census estimate: ~84,872 (city proper)
    // Calcasieu Parish Metro: ~215,000+
    // Source: https://www.census.gov/quickfacts/lakecharlescitylouisiana
    population: 84872,
    tagline: 'The Festival Capital of Louisiana',
  },

  // Economic data (unique to Lake Charles)
  economics: {
    // Annual consumer spending estimate for Lake Charles metro area
    // DECISION: Using conservative estimate ($5B). Original design included $5-8B range,
    // but range removed to improve clarity and reduce confusion from duplicate displays.
    annualConsumerSpending: '$5B',
    // Annual industrial/commercial (petrochemical, gaming, shipping)
    annualIndustrialSpending: '$10B+',
    // 0.2% of consumer spending at 30% adoption (community-controlled fund)
    projectedCommunityTreasury: '$3M/year',
    // Separate 0.2% stream to city (NOT taken from community treasury) at 30% adoption
    projectedCityRevenue: '$3M/year',
    // Data source documentation
    dataSource: {
      consumerSpending:
        'US Census Bureau QuickFacts 2023 + BEA personal income estimates for Calcasieu Parish',
      industrialSpending:
        'Louisiana Office of Commerce - Industrial Analysis; includes petrochemical, gaming, port shipping, energy sector',
    },
  },

  // Lake Charles branding (inherit Louisiana defaults)
  branding: {
    // Uses Louisiana colors (primaryColor, accentColor)
    // Override if Lake Charles needs custom colors
  },

  // Database config - UNIFIED SCHEMA (not legacy per-city schema)
  database: {
    schema: 'community_app_landingpages_all_cities',
    host: 'localhost',
    port: 5432,
  },

  // API endpoints - Unified multi-city API
  api: {
    // Allow per-city env override; defaults are safe public values
    baseUrl: (import.meta as any).env?.VITE_API_BASE_URL || 'https://api.toolboxlogic.com',
    routePrefix: '/api/v1/louisiana/lake-charles',
    healthEndpoint: '/health',
    interestEndpoint: '/api/v1/louisiana/lake-charles/interest-form',
    championEndpoint: '/api/v1/louisiana/lake-charles/champion-application',
    consultationEndpoint: '/api/v1/louisiana/lake-charles/consultation-request',
    vendorEndpoint: '/api/v1/louisiana/lake-charles/vendor-waitlist',
  },

  // Deployment config
  deployment: {
    subdomain: 'lakecharles',
    fullDomain: 'lakecharles.toolboxlogic.com',
    cloudflareProject: 'lakecharles-la-landing',
    port: 5175, // Dev server port
  },

  // SEO & Meta
  seo: {
    title: 'Lake Charles Community App | ToolboxLogic',
    description:
      "Organize Lake Charles neighborhoods with powerful civic tools: voting, petitions, group buying & more. Join the waitlist for Louisiana's first community-led platform for civic engagement.",
    keywords:
      'Lake Charles community, civic engagement, community organizing, Lake Charles residents, Louisiana voting, citizen empowerment, Calcasieu Parish',
    ogImage: '/media_lakecharles/og-lakecharles-1200x630.png',
    favicon: '/media_lakecharles/favicon.ico',
    canonical: 'https://lakecharles.toolboxlogic.com/',
    locale: 'en_US',
  },

  // Content - Hero Section
  content: {
    hero: {
      badgeText: 'Launching Soon in Lake Charles',
      headline: {
        line1: 'Your Voice, Amplified.',
        line2: 'The Tools Lake Charles Needs',
        line3: 'to Organize and Be Heard.',
      },
      subheadline:
        "A City Council vote shouldn't be the final word. What if you had the power to organize your community, prove public consensus, and hold decision-makers accountable?",
      bodyText:
        "We're exploring bringing a new community platform to Lake Charles, Louisiana and we want to know if you're in.",
      primaryCta: {
        text: "Yes, I'm Interested",
      },
      secondaryCta: {
        text: 'Learn More',
      },
      heroImage: {
        src: '/media_lakecharles/lake-charles-community-app.webp',
        mobileSrc: '/media_lakecharles/lake-charles-community-app.webp',
        alt: 'Lake Charles community engagement platform interface empowering residents in Lake Charles Louisiana to organize, prove consensus, and influence civic decisions',
        width: 1024,
        height: 1024,
      },
    },

    // Features Section
    features: {
      title: 'A Platform Built for Community Power',
      subtitle:
        "We're ToolboxLogic, we specialize in Community Apps. Currently, we're exploring bringing a Lake Charles-specific version to you, designed to help residents:",
    },

    // Interest Form
    interestForm: {
      title: 'Interested in a Community App for Lake Charles?',
      description:
        "We're gauging interest right now. Fill out this quick form to stay updated and let us know what matters most to you. No commitment, just exploring the possibility together.",
      successMessage:
        "We'll keep you updated as we explore bringing this platform to Lake Charles.",
    },

    // Local Business Schema Data
    localBusiness: {
      name: 'Lake Charles Community App',
      url: 'https://lakecharles.toolboxlogic.com/',
      type: 'CommunityOrganization',
      telephone: '+1-337-802-4902', // Placeholder
      areaServed: 'Lake Charles, LA',
      address: {
        addressLocality: 'Lake Charles',
        addressRegion: 'LA',
        postalCode: '70601',
        addressCountry: 'US',
      },
      geo: {
        latitude: 30.2266,
        longitude: -93.2174,
      },
      makesOffer: [
        'Community engagement tools',
        'Consensus measurement',
        'Local civic participation platform',
        'Resident feedback collection',
      ],
    },
  },

  // Feature toggles
  features: {
    enableScrollAnimation: false, // Disable ScrollMagic (causes layout issues)
    animationIntensity: 'subtle' as const,
    enableVideoDemos: true, // Lake Charles has video demos
    enableChampionProgram: true,
    enableChampionsDashboard: true, // Lake Charles-specific feature
    enablePlanSelector: true,
    enableFacebookGroups: true,
  },

  // Asset paths
  assets: {
    mediaFolder: '/media_lakecharles',
    logoPath: '/media_lakecharles/lake-charles-logo.svg',
  },

  // Links
  links: {
    championApplicationUrl: '/champions/apply',
    championsDashboardUrl: '/champions/dashboard',
    termsUrl: '/terms',
    privacyUrl: '/privacy',
    vendorsUrl: '/vendors',
    cityPartnershipUrl: '/city-partnership',
  },

  // Analytics (optional)
  analytics: {
    googleAnalyticsId: (import.meta as any).env?.VITE_GA_ID || '',
    facebookPixelId: (import.meta as any).env?.VITE_FB_PIXEL || '',
  },
};

// Merge state defaults with Lake Charles overrides
export const lakeCharlesConfig: CityConfig = mergeStateConfig(
  louisianaStateConfig,
  lakeCharlesOverrides,
) as CityConfig;

export default lakeCharlesConfig;
